// Casing & cementing in the rotary era (M2_SPEC §3).
//   runCasing(..., { cement })  → CASING timer → CEMENTING (wait on cement, wocDays) → back to work.
//   Cemented strings seal pressure (mud.js pressureShoe) and add shoeFracBonus at the shoe.
//   Uncemented casing (1901+): gas/water behind pipe migrates (0.5%/day) → casinghead gas / water in the oil.
//   Steel casing has a collapse rating: MW or pore behind it above collapsePpg → 2%/day collapse risk.
//   Texas waste rules (rule37 flag, 1919+): uncemented surface casing over fresh-water sands → $200 fine per well
//   at the yearly inspection (30% chance per field).
import { chance } from '../core/rng.js';
import { dayToDate } from '../core/time.js';
import { eqDef, fieldDef, stat, unitPrice, eraDollars, isAvailable, yearOf, defsByCat } from './defs.js';
import { strataAt } from './geo.js';
import { mudWeight, isPermeable } from './mud.js';
import { clamp, notify, log, spend, fmt$ } from './util.js';
import { flagOn } from './m2util.js';

export const ANNULAR_PER_DAY = 0.005;
export const COLLAPSE_PER_DAY = 0.02;
export const RULE37_FINE = 200; // 1919 $
export const RULE37_INSPECT = 0.3;

export function isCementDef(def) {
  return !!def && def.cat === 'casing' && !!stat(def, 'perJob', false) && stat(def, 'wocDays', null) != null;
}

export function cementDefs(state) {
  return defsByCat('casing').filter((d) => isCementDef(d) && isAvailable(d, state));
}

// Best cement available now (shortest wait on cement), or null.
export function bestCement(state) {
  const list = cementDefs(state);
  list.sort((a, b) => stat(a, 'wocDays', 10) - stat(b, 'wocDays', 10));
  return list[0] ?? null;
}

export function cementPrice(state, def) { return def ? unitPrice(state, def) : 0; }

// Validate a cement choice for runCasing. Returns { ok, def, price } or { ok:false, msg }.
export function checkCement(state, cementId) {
  if (!cementId) return { ok: true, def: null, price: 0 };
  const def = eqDef(cementId === true ? bestCement(state)?.id : cementId);
  if (!isCementDef(def)) return { ok: false, msg: 'Not a cement job.' };
  if (!isAvailable(def, state)) return { ok: false, msg: `${def.name} is not available until ${def.year}.` };
  return { ok: true, def, price: cementPrice(state, def) };
}

const lastCasing = (well) => (well.casing?.length ? well.casing[well.casing.length - 1] : null);

// Start the wait-on-cement for the deepest string. setTask comes from drilling.js (passed in to avoid a cycle).
export function startCementing(state, well, cementId, resume, setTask) {
  const def = eqDef(cementId);
  const c = lastCasing(well);
  if (!def || !c) return false;
  c.cementing = cementId;
  well.status = 'CEMENTING';
  const days = Math.max(1, stat(def, 'wocDays', 2));
  setTask(state, well, 'CEMENTING', days, { cement: cementId, resume });
  log(state, `${well.name}: cementing the ${Math.round(c.bottom)}-ft string (${def.name}); waiting on cement ${days} days.`, 'info', { wellId: well.id });
  return true;
}

export function onCementSet(state, well, t) {
  const c = (well.casing ?? []).slice().reverse().find((x) => x.cementing === t.data.cement) ?? lastCasing(well);
  if (c) { c.cemented = t.data.cement; delete c.cementing; }
  well.lastShoeCemented = true;
  emitCement(state, well, true, c, t.data.cement);
  if (well.annular && !uncementedOver(state, well, well.annular.kind)) well.annular = null;
  return t.data.resume ?? 'DRILLING';
}

// Squeeze job on the deepest uncemented string (fixes annular flow). Producing wells lose wocDays of production.
export function cementCasingImpl(state, well, cementId, setTask) {
  const chk = checkCement(state, cementId ?? true);
  if (!chk.ok) return chk;
  if (!chk.def) return { ok: false, msg: 'No cement service available yet (Perkins 1910, Halliburton 1920).' };
  const c = (well.casing ?? []).slice().reverse().find((x) => !x.cemented && !x.cementing);
  if (!c) return { ok: false, msg: 'Every casing string is already cemented.' };
  const drilling = ['DRILLING', 'PAUSED', 'AT_TD', 'DRY'].includes(well.status) && well.rigId;
  const producing = ['PRODUCING', 'SHUT_IN', 'DEPLETED'].includes(well.status);
  if (!drilling && !producing) return { ok: false, msg: `Can't cement while ${well.status}.` };
  if (!spend(state, chk.price, 'consumables')) return { ok: false, msg: `${chk.def.name} costs ${fmt$(chk.price)}.` };
  well.cost += chk.price;
  const woc = Math.max(1, stat(chk.def, 'wocDays', 2));
  if (drilling) {
    const resume = well.status === 'PAUSED' ? 'PAUSED' : well.status === 'AT_TD' ? 'AT_TD' : well.status === 'DRY' ? 'DRY' : 'DRILLING';
    c.cementing = chk.def.id;
    well.status = 'CEMENTING';
    setTask(state, well, 'CEMENTING', woc, { cement: chk.def.id, resume });
  } else {
    c.cemented = chk.def.id;
    emitCement(state, well, true, c, chk.def.id);
    if (well.prod) well.prod.forcedUntil = Math.max(well.prod.forcedUntil ?? 0, state.day + woc);
    if (well.annular && !uncementedOver(state, well, well.annular.kind)) well.annular = null;
  }
  return { ok: true, msg: `${chk.def.name} squeezed behind the ${Math.round(c.bottom)}-ft string (${fmt$(chk.price)}, ${woc} d wait on cement).` };
}

// Is a gas (or water) zone sitting behind uncemented casing with no cemented string over it?
export function uncementedOver(state, well, kind = 'gas') {
  const strata = strataAt(fieldDef(well.fieldId), state.fields[well.fieldId].plots[well.plot]);
  const cementedTo = (well.casing ?? []).filter((c) => c.cemented).reduce((a, c) => Math.max(a, c.bottom), 0);
  for (const c of well.casing ?? []) {
    if (c.cemented || (c.day != null && dayToDate(c.day).year < 1901)) continue;
    for (const s of strata) {
      if (s.top >= c.bottom || s.bottom <= c.top) continue;
      if (s.bottom <= cementedTo + 1e-6) continue;
      if (kind === 'gas' ? s.gas : s.water) return s;
    }
  }
  return null;
}

// Daily casing checks for one well (M2, any rig type, casing run 1901+).
// Cache: does this casing program leave a gas/water zone behind uncemented 1901+ pipe? (keyed by the casing signature)
const ANN_CACHE = new WeakMap();
function annularCandidates(state, well) {
  const sig = well.casing.map((c) => `${c.bottom}:${c.cemented ? 1 : 0}:${c.day ?? 0}`).join('|');
  const hit = ANN_CACHE.get(well);
  if (hit && hit.sig === sig) return hit;
  const v = { sig, gas: uncementedOver(state, well, 'gas'), water: uncementedOver(state, well, 'water') };
  ANN_CACHE.set(well, v);
  return v;
}

export function casingDay(state, well) {
  if (!well.casing?.length || well.annular || ['PLUGGED', 'DRY', 'DEPLETED'].includes(well.status)) return null;
  const cand = annularCandidates(state, well);
  if (!cand.gas && !cand.water) return null;
  // annular migration
  if (!well.annular && ['DRILLING', 'PAUSED', 'AT_TD', 'PRODUCING', 'SHUT_IN', 'COMPLETING', 'TRIPPING'].includes(well.status)) {
    for (const kind of ['gas', 'water']) {
      const s = cand[kind];
      if (s && chance(state, ANNULAR_PER_DAY)) {
        well.annular = { kind, day: state.day, stratum: s.name ?? s.rock };
        notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'ANNULAR_GAS', level: 'warn',
          text: `${well.name}: ${kind === 'gas' ? 'casinghead gas is bubbling up' : 'salt water is coming up'} behind the uncemented casing from the ${s.name ?? s.rock}. Production −20% until the casing is squeezed with cement.` });
        break;
      }
    }
  }
  return null;
}

// Collapse risk while the hole is being worked: returns the failed string or null.
export function collapseRoll(state, well) {
  if (!well.casing?.length || state._rush) return null;
  const mw = mudWeight(well);
  const strata = strataAt(fieldDef(well.fieldId), state.fields[well.fieldId].plots[well.plot]);
  for (const c of well.casing) {
    const rating = stat(eqDef(c.defId), 'collapsePpg', null);
    if (!rating) continue;
    let behind = 8.4;
    for (const s of strata) if (s.top < c.bottom && s.bottom > c.top && isPermeable(s)) behind = Math.max(behind, s.pore ?? 8.6);
    const load = Math.max(mw, behind);
    if (load > rating + 1e-9 && chance(state, COLLAPSE_PER_DAY)) return c;
  }
  return null;
}

// Yearly Texas waste/rule-37 inspection (1 Jan from the rule37 flag): 30% per field, $200 (1919 $) per offending well.
export function yearlyInspections(state) {
  const { month, dom } = dayToDate(state.day);
  if (month !== 1 || dom !== 1 || !flagOn(state, 'rule37')) return;
  const byField = {};
  for (const w of Object.values(state.wells)) {
    if (['PLUGGED', 'DRY'].includes(w.status) || fieldDef(w.fieldId)?.regionId !== 'texas' || w.ownerOperatorId) continue;
    if (uncementedOver(state, w, 'water')) (byField[w.fieldId] ??= []).push(w);
  }
  for (const [fid, list] of Object.entries(byField)) {
    if (!chance(state, RULE37_INSPECT)) continue;
    const fine = eraDollars(state, RULE37_FINE, 1919) * list.length;
    spend(state, fine, 'other', false);
    notify(state, 'news', { fieldId: fid, kind: 'regulation', level: 'warn',
      text: `Railroad Commission inspectors at ${fieldDef(fid)?.name}: ${list.length} well(s) with uncemented casing over fresh-water sands. Fined ${fmt$(fine)}.` });
  }
}

export const clampPpg = (x) => clamp(x, 8, 20);

// Bus event `cement` { wellId, fieldId, ok, shoe, shoeFracBonus, wocDays, text, pause } — job set (ok) or skipped (ok:false).
export function emitCement(state, well, ok, c, cementId, why = '') {
  const def = cementId ? eqDef(cementId) : null;
  const shoe = Math.round(c?.bottom ?? well.shoe ?? 0);
  notify(state, 'cement', { wellId: well.id, fieldId: well.fieldId, ok, shoe, cementDefId: cementId ?? null, shoeFracBonus: def ? stat(def, 'shoeFracBonus', 0.7) : 0, wocDays: def ? stat(def, 'wocDays', 2) : 0, pause: !ok,
    level: ok ? 'info' : 'warn',
    text: ok ? `${well.name}: cement set behind the ${shoe}-ft string (${def?.name ?? 'cement'}); the shoe holds pressure now.` : `${well.name}: the ${shoe}-ft string went in WITHOUT cement${why ? ` (${why})` : ''} — it won't seal pressure.` });
}
